import React from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { isPrivilegedUser, isOwnTask } from "../../../Common/permissions";
import TaskDetails from "../TaskCalendar/TaskDetails";
import BulkUpload from "./BulkUpload";
import Create from "./Create";
import Edit from "./Edit";
import StatusUpdate from "./StatusUpdate";

const List = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [bulkDialogVisible, setBulkDialogVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("exclude_done");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [developerFilter, setDeveloperFilter] = useState(null);
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [error, setError] = useState("");
  const [highlightedItems, setHighlightedItems] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const toast = useRef(null);
  const filterPanelRef = useRef(null);

  // Admins and department heads get full access; everyone else is restricted.
  const privileged = isPrivilegedUser(currentUser);
  const canManageTask = (task) =>
    privileged || (currentUser && isOwnTask(task, currentUser.id));

  // Default (neutral) filter values — used to count "active" filters and to reset.
  const STATUS_DEFAULT = "exclude_done";
  const SCHEDULE_DEFAULT = "all";

  const activeFilterCount =
    (statusFilter !== STATUS_DEFAULT ? 1 : 0) +
    (scheduleFilter !== SCHEDULE_DEFAULT ? 1 : 0) +
    (developerFilter ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter(STATUS_DEFAULT);
    setScheduleFilter(SCHEDULE_DEFAULT);
    setDeveloperFilter(null);
  };

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId) => {
    setHighlightedItems((prev) => ({
      ...prev,
      [`task-${itemId}`]: true,
    }));

    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[`task-${itemId}`];
        return newState;
      });
    }, 2000);
  };

  // Handle adding a new task without refreshing all data
  const handleAddNewTask = (newTaskData) => {
    if (!newTaskData) return;

    setTasks((prevTasks) => {
      const exists = prevTasks.some((task) => task.id === newTaskData.id);
      if (exists) return prevTasks;
      highlightItem(newTaskData.id);
      return [...prevTasks, newTaskData];
    });

    setCreateDialogVisible(false);

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task created successfully",
      life: 3000,
    });
  };

  // Handle bulk-created tasks from the CSV upload modal.
  const handleBulkCreated = (newTasks) => {
    if (!Array.isArray(newTasks) || newTasks.length === 0) return;
    setTasks((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const additions = newTasks.filter((t) => t && t.id && !existingIds.has(t.id));
      additions.forEach((t) => highlightItem(t.id));
      return [...prev, ...additions];
    });
    setBulkDialogVisible(false);
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: `${newTasks.length} task(s) created successfully`,
      life: 3000,
    });
  };

  // Handle updating a task without refreshing all data
  const handleUpdateTask = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          const taskWithUpdates = {
            ...task,
            ...updatedTaskData,
            developers: Array.isArray(updatedTaskData.developers)
              ? updatedTaskData.developers
              : updatedTaskData.developer_ids?.map((id) => ({ id })) || task.developers || [],
          };
          highlightItem(taskWithUpdates.id);
          return taskWithUpdates;
        }
        return task;
      });
    });

    setEditDialogVisible(false);

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task updated successfully",
      life: 3000,
    });
  };

  // Handle updating task status without refreshing all data
  const handleUpdateTaskStatus = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          const taskWithUpdates = { ...task, ...updatedTaskData };
          highlightItem(taskWithUpdates.id);
          return taskWithUpdates;
        }
        return task;
      });
    });

    setStatusDialogVisible(false);

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task status updated successfully",
      life: 3000,
    });
  };

  // Status options for filtering
  const statusOptions = [
    { label: "Active Tasks (excl. Done)", value: "exclude_done" },
    { label: "All Statuses", value: "all" },
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Schedule (planned-date) options for filtering
  const scheduleOptions = [
    { label: "All Tasks", value: "all" },
    { label: "Scheduled", value: "scheduled" },
    { label: "Unscheduled", value: "unscheduled" },
  ];

  // Fetch tasks and developers on mount
  useEffect(() => {
    fetchCurrentUser();
    fetchData();
    fetchDevelopers();
  }, []);

  // Load the logged-in user and, for a normal member, default the view to their
  // own tasks (they can still clear/change the developer filter to view others).
  const fetchCurrentUser = async () => {
    try {
      const res = await Http.get("/me");
      const user = res.data?.data || res.data;
      setCurrentUser(user);
      if (!isPrivilegedUser(user) && user?.id) {
        setDeveloperFilter(user.id);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await Http.get("/task");
      if (response.data && response.data.data) {
        setTasks(response.data.data);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setError("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDevelopers = async () => {
    try {
      const res = await Http.get("/staff-department-wise/30");
      const options = [{ label: "All Developers", value: null }];
      if (Array.isArray(res.data)) {
        res.data.forEach((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          const displayName = fullName || dev.name || `ID: ${dev.id}`;
          options.push({ label: displayName, value: dev.id });
        });
      }
      setDeveloperOptions(options);
    } catch (error) {
      console.error("Error fetching developers:", error);
    }
  };

  // Delete task handler
  const confirmDelete = (task) => {
    confirmDialog({
      message: `Are you sure you want to delete task "${task.title}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(task.id),
    });
  };

  const handleDelete = async (taskId) => {
    try {
      await Http.delete(`/task/${taskId}`);
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Task deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete task",
        life: 3000,
      });
    }
  };

  // Duplicate task handler — clones the task server-side and adds the new row
  const handleDuplicate = async (task) => {
    try {
      const response = await Http.post(`/task/${task.id}/duplicate`);
      const newTaskData = response.data?.data || response.data;
      if (newTaskData && newTaskData.id) {
        handleAddNewTask(newTaskData);
      }
    } catch (error) {
      console.error("Error duplicating task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to duplicate task",
        life: 3000,
      });
    }
  };

  // Format date as dd/mm/yyyy by parsing the string directly — avoids timezone shift
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const datePart = dateString.split("T")[0];
    if (!datePart || !datePart.includes("-")) return "N/A";
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  };

  const getUserName = (user) => {
    if (!user) return "N/A";
    return user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || `ID: ${user.id}`;
  };

  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) {
      return <span className="text-muted">None assigned</span>;
    }

    if (developers.length === 1) {
      return <span>{getUserName(developers[0])}</span>;
    }

    return (
      <>
        <span>{getUserName(developers[0])}</span>
        <span className="ms-1 badge bg-secondary">{`+${developers.length - 1} more`}</span>
      </>
    );
  };

  // Template for status column
  const statusBodyTemplate = (rowData) => {
    let statusClassName = "";

    switch (rowData.status) {
      case "To Do":
        statusClassName = "bg-secondary";
        break;
      case "In Progress":
        statusClassName = "bg-primary";
        break;
      case "Review":
        statusClassName = "bg-warning";
        break;
      case "Done":
        statusClassName = "bg-success";
        break;
      default:
        statusClassName = "bg-secondary";
    }

    return <Tag className={statusClassName}>{rowData.status}</Tag>;
  };

  // Template for project/story column
  const projectBodyTemplate = (rowData) => {
    if (rowData.story) {
      return (
        <div>
          <div>{rowData.story.title}</div>
          <small className="text-muted">{rowData.project?.title || "N/A"}</small>
        </div>
      );
    } else {
      return <div>{rowData.project?.title || "N/A"}</div>;
    }
  };

  // Template for task type column
  const taskTypeBodyTemplate = (rowData) => {
    const taskType = rowData.task_type;
    if (!taskType) return <span>N/A</span>;

    return (
      <div className="d-flex align-items-center">
        {taskType.icon && <i className={taskType.icon} style={{ marginRight: "5px", color: taskType.color }}></i>}
        <span>{taskType.task_type}</span>
      </div>
    );
  };

  // Template for label column
  const labelBodyTemplate = (rowData) => {
    const label = rowData.label;
    if (!label) return <span>N/A</span>;

    return (
      <Tag
        style={{
          backgroundColor: label.color || "#ccc",
          color: getContrastColor(label.color),
        }}
      >
        {label.name}
      </Tag>
    );
  };

  // Helper to determine text color based on background color
  const getContrastColor = (hexColor) => {
    if (!hexColor) return "#ffffff";

    hexColor = hexColor.replace("#", "");

    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  // Inclusive day count between two date strings (YYYY-MM-DD or ISO).
  const planDays = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(String(start).split("T")[0]);
    const e = new Date(String(end).split("T")[0]);
    if (isNaN(s) || isNaN(e)) return null;
    const days = Math.round((e - s) / 86400000) + 1;
    return days > 0 ? days : null;
  };

  // Hour count between two datetime strings, rounded to 1 decimal.
  const actualHours = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return null;
    const hours = (e - s) / 3600000;
    if (hours <= 0) return null;
    return Number.isInteger(hours) ? hours : Number(hours.toFixed(1));
  };

  // Template for timeline column — packs Plan + Actual + durations into one cell.
  const timelineBodyTemplate = (rowData) => {
    const days = planDays(rowData.start_date, rowData.end_date);
    const estimated = rowData.estimated_time;

    const planMeta = [days ? `${days}d` : null, estimated || null].filter(Boolean).join(" · ");

    const planRow = !rowData.start_date && !rowData.end_date ? (
      <div style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
        <strong>Plan: </strong>
        <span className="badge bg-secondary">Unscheduled</span>
        {estimated && <span className="text-muted ms-1">· {estimated}</span>}
      </div>
    ) : (
      <div style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
        <strong>Plan: </strong>
        {formatDate(rowData.start_date)} → {formatDate(rowData.end_date)}
        {planMeta && <span className="text-muted ms-1">· {planMeta}</span>}
      </div>
    );

    let actualRow;
    if (!rowData.actual_start_date) {
      actualRow = (
        <div style={{ fontSize: "0.85rem" }} className="text-muted">
          <strong>Actual: </strong>— not started
        </div>
      );
    } else if (!rowData.actual_end_date) {
      actualRow = (
        <div style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
          <strong>Actual: </strong>
          {formatDate(rowData.actual_start_date)} → <span className="text-muted">… (running)</span>
        </div>
      );
    } else {
      const hours = actualHours(rowData.actual_start_date, rowData.actual_end_date);
      actualRow = (
        <div style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
          <strong>Actual: </strong>
          {formatDate(rowData.actual_start_date)} → {formatDate(rowData.actual_end_date)}
          {hours != null && <span className="text-muted ms-1">· {hours}h</span>}
        </div>
      );
    }

    return (
      <div>
        {planRow}
        {actualRow}
      </div>
    );
  };

  // Open the details modal for a task, fetching the full record (with status
  // history) since the list payload is trimmed.
  const openDetails = async (task) => {
    setDetailsDialogVisible(true);
    setDetailsLoading(true);
    setDetailsTask(null);
    try {
      const response = await Http.get(`/task/${task.id}`);
      setDetailsTask(response.data?.data || null);
    } catch (error) {
      console.error("Error fetching task details:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load task details.",
        life: 3000,
      });
      setDetailsDialogVisible(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Template for actions column. Everyone can open the details modal; admins/heads
  // get every action; a normal member can only change the status of a task
  // assigned to them, and gets no edit/copy/delete on any task.
  const actionsBodyTemplate = (rowData) => {
    const btnStyle = { width: "26px", height: "26px", padding: "0" };
    const canChangeStatus = canManageTask(rowData);

    return (
      <div className="d-flex gap-1">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-secondary p-button-sm"
          style={btnStyle}
          onClick={() => openDetails(rowData)}
          tooltip="Details"
          tooltipOptions={{ position: "top" }}
        />
        {privileged && (
          <>
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-success p-button-sm"
              style={btnStyle}
              onClick={() => {
                setSelectedTask(rowData);
                setEditDialogVisible(true);
              }}
              tooltip="Edit"
              tooltipOptions={{ position: "top" }}
            />
            <Button
              icon="pi pi-copy"
              className="p-button-rounded p-button-help p-button-sm"
              style={btnStyle}
              onClick={() => handleDuplicate(rowData)}
              tooltip="Duplicate"
              tooltipOptions={{ position: "top" }}
            />
          </>
        )}
        {canChangeStatus && (
          <Button
            icon="pi pi-sync"
            className="p-button-rounded p-button-info p-button-sm"
            style={btnStyle}
            onClick={() => {
              setSelectedTask(rowData);
              setStatusDialogVisible(true);
            }}
            tooltip="Update Status"
            tooltipOptions={{ position: "top" }}
          />
        )}
        {privileged && (
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-sm"
            style={btnStyle}
            onClick={() => confirmDelete(rowData)}
            tooltip="Delete"
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>
    );
  };

  // Filter tasks based on search term, status and developer
  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      searchTerm === "" ||
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" ||
      !statusFilter ||
      (statusFilter === "exclude_done" ? task.status !== "Done" : task.status === statusFilter);

    const matchesDeveloper =
      !developerFilter ||
      (task.developers && task.developers.some((dev) => dev.id === developerFilter));

    const matchesSchedule =
      scheduleFilter === "all" ||
      (scheduleFilter === "scheduled" ? !!task.start_date : !task.start_date);

    return matchesSearch && matchesStatus && matchesDeveloper && matchesSchedule;
  });

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Tasks </h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center gap-2">
              <span className="p-input-icon-left" style={{ width: "280px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <Button
                className="p-button-outlined p-button-secondary"
                onClick={(e) => filterPanelRef.current.toggle(e)}
                aria-label="Filters"
              >
                <i className="pi pi-filter me-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge value={activeFilterCount} severity="info" className="ms-2" />
                )}
              </Button>
              <OverlayPanel ref={filterPanelRef} className="task-filter-overlay" style={{ width: "auto" }}>
                <div className="d-flex flex-row align-items-end gap-3">
                  <div style={{ minWidth: "180px" }}>
                    <label className="form-label small mb-1">Status</label>
                    <Dropdown
                      value={statusFilter}
                      options={statusOptions}
                      onChange={(e) => {
                        setStatusFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      className="w-100"
                      scrollHeight="220px"
                    />
                  </div>
                  <div style={{ minWidth: "160px" }}>
                    <label className="form-label small mb-1">Schedule</label>
                    <Dropdown
                      value={scheduleFilter}
                      options={scheduleOptions}
                      onChange={(e) => {
                        setScheduleFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      className="w-100"
                      scrollHeight="220px"
                    />
                  </div>
                  <div style={{ minWidth: "180px" }}>
                    <label className="form-label small mb-1">Developer</label>
                    <Dropdown
                      value={developerFilter}
                      options={developerOptions}
                      onChange={(e) => {
                        setDeveloperFilter(e.value);
                        filterPanelRef.current?.hide();
                      }}
                      placeholder="All developers"
                      className="w-100"
                      scrollHeight="220px"
                      showClear={!!developerFilter}
                    />
                  </div>
                  <Button
                    label="Clear all"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => {
                      clearFilters();
                      filterPanelRef.current?.hide();
                    }}
                    disabled={activeFilterCount === 0}
                  />
                </div>
              </OverlayPanel>
            </div>
            {privileged && (
              <div className="d-flex gap-2">
                <Button
                  label="Upload CSV"
                  icon="pi pi-upload"
                  className="p-button-info"
                  onClick={() => setBulkDialogVisible(true)}
                />
                <Button label="Create Task" icon="pi pi-plus" className="p-button-success" onClick={() => setCreateDialogVisible(true)} />
              </div>
            )}
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <Toast ref={toast} />
            <ConfirmDialog />

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <ProgressSpinner />
                <p className="mt-2">Loading tasks...</p>
              </div>
            ) : (
              <DataTable
                value={filteredTasks}
                paginator
                rows={100}
                rowsPerPageOptions={[100, 150, 250]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} tasks"
                responsiveLayout="scroll"
                emptyMessage="No tasks found"
                sortField="id"
                sortOrder={-1}
                rowClassName={(rowData) => {
                  const classes = [];
                  if (highlightedItems[`task-${rowData.id}`]) classes.push("highlight-row");
                  // Dim tasks that don't belong to a normal member so it's clear
                  // they're read-only / not theirs.
                  if (!privileged && !canManageTask(rowData)) classes.push("pm-row-inactive");
                  return classes.join(" ");
                }}
              >
                <Column field="title" header="Title" sortable />
                <Column body={taskTypeBodyTemplate} header="Task Type" sortable sortField="task_type.task_type" />
                <Column body={projectBodyTemplate} header="Project/Story" />
                <Column body={timelineBodyTemplate} header="Timeline" />
                <Column body={statusBodyTemplate} field="status" header="Status" sortable />
                <Column body={(rowData) => renderDevelopers(rowData.developers)} header="Developers" />
                <Column field="points" header="Points" sortable />
                <Column body={labelBodyTemplate} header="Label" />
                <Column body={actionsBodyTemplate} header="Actions" />
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog
        header="Upload Tasks from CSV"
        visible={bulkDialogVisible}
        style={{ width: "90vw" }}
        onHide={() => setBulkDialogVisible(false)}
        maximizable
      >
        <BulkUpload setVisible={setBulkDialogVisible} onSuccess={handleBulkCreated} />
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog header="Create Task" visible={createDialogVisible} style={{ width: "80vw" }} onHide={() => setCreateDialogVisible(false)} maximizable>
        <Create
          setVisible={setCreateDialogVisible}
          onSuccess={(newTaskData) => {
            if (newTaskData && newTaskData.id) {
              handleAddNewTask(newTaskData);
            } else {
              setCreateDialogVisible(false);
            }
          }}
        />
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog header="Edit Task" visible={editDialogVisible} style={{ width: "80vw" }} onHide={() => setEditDialogVisible(false)} maximizable>
        {selectedTask && <Edit taskId={selectedTask.id} setVisible={setEditDialogVisible} onSuccess={handleUpdateTask} />}
      </Dialog>

      {/* Task Details Dialog */}
      <Dialog
        header="Task Details"
        visible={detailsDialogVisible}
        style={{ width: "80vw" }}
        onHide={() => setDetailsDialogVisible(false)}
        maximizable
      >
        {detailsLoading ? (
          <div className="text-center p-5">
            <ProgressSpinner />
            <p className="mt-2">Loading task details...</p>
          </div>
        ) : (
          <TaskDetails task={detailsTask} showActions={false} />
        )}
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog header="Update Task Status" visible={statusDialogVisible} style={{ width: "50vw" }} onHide={() => setStatusDialogVisible(false)}>
        {selectedTask && (
          <StatusUpdate
            taskId={selectedTask.id}
            currentStatus={selectedTask.status}
            setVisible={setStatusDialogVisible}
            onSuccess={handleUpdateTaskStatus}
          />
        )}
      </Dialog>
    </div>
  );
};

export default List;
